import { Component, Input } from '@angular/core';
import { Color, ScaleType } from '@swimlane/ngx-charts';
import { curveCardinal } from 'd3-shape';

@Component({
    selector: 'app-conv-hneb-chart',
    templateUrl: './conv-hneb-chart.component.html',
    styleUrl: './conv-hneb-chart.component.scss',
    standalone: false
})
export class ConvHnebChartComponent {
	@Input() area_type: string = 'nose';
	@Input() data: any = {};
	@Input() head_positions: any[] = [];

	chartData: any[] = [];

	curve: any = curveCardinal;

	legend = false;
  animations = false;
  autoScale = false;
  showGridLines = true;
  roundDomains = false;
  xAxis = true;
  yAxis = true;
  showYAxisLabel = true;
  showXAxisLabel = true;
	min = -50;
	max = 50;
	colorScheme: string | Color = {
		name: 'myScheme',
		selectable: true,
    group: ScaleType.Ordinal,
    domain: ['#FF0000', '#FFFF00', '#008000', '#800080', '#FF1493', '#00FFFF']
  };

	ready = false;

	videoTime = '00:00';

	calculateChartData(currentTime: number) {
		const start = currentTime - 20;
		
		if (this.area_type === 'head') {
			const dataChart: any = [
				{ name: 'Head Pose', series: [] }
			];
	
			for (let index = start; index <= currentTime; index++) {
				const time = this.convertToTimerFormat(index);
	
				let headPose;
				let positions: any[] = [];
	
				if (index < this.head_positions.length) {
					headPose = this.countUniqueStrings(this.head_positions[index]);
					positions = this.arrangeStrings(this.head_positions[index]);
				} else {
					headPose = 0;
				}
	
				dataChart[0].series.push({ name: time, value: headPose, original: positions });
			}
	
			this.chartData = [...dataChart];
		}

		if (this.area_type === 'nose') {
			const dataChart: any = [
				{ name: 'outer_nose_R', series: [] },
				{ name: 'outer_nose_G', series: [] },
				{ name: 'outer_nose_B', series: [] },
				{ name: 'inner_nose_R', series: [] },
				{ name: 'inner_nose_G', series: [] },
				{ name: 'inner_nose_B', series: [] }
			];
	
			for (let index = start; index <= currentTime; index++) {
				const time = this.convertToTimerFormat(index);
	
				let outer_nose_R;
				let outer_nose_G;
				let outer_nose_B;

				let inner_nose_R;
				let inner_nose_G;
				let inner_nose_B;
	
				if (index < this.data.outer_nose.length) {
					outer_nose_R = +(this.data.outer_nose[index][0]);
					outer_nose_G = +(this.data.outer_nose[index][1]);
					outer_nose_B = +(this.data.outer_nose[index][2]);
				} else {
					outer_nose_R = 0;
					outer_nose_G = 0;
					outer_nose_B = 0;
				}

				if (index < this.data.inner_nose.length) {
					inner_nose_R = +(this.data.inner_nose[index][0]);
					inner_nose_G = +(this.data.inner_nose[index][1]);
					inner_nose_B = +(this.data.inner_nose[index][2]);
				} else {
					inner_nose_R = 0;
					inner_nose_G = 0;
					inner_nose_B = 0;
				}
	
				dataChart[0].series.push({ name: time, value: outer_nose_R });
				dataChart[1].series.push({ name: time, value: outer_nose_G });
				dataChart[2].series.push({ name: time, value: outer_nose_B });
	
				dataChart[3].series.push({ name: time, value: inner_nose_R });
				dataChart[4].series.push({ name: time, value: inner_nose_G });
				dataChart[5].series.push({ name: time, value: inner_nose_B });
			}
	
			this.chartData = [...dataChart];
		}

		if (this.area_type === 'eyebrow') {
			const dataChart: any = [
				{ name: 'left_eyebrow_R', series: [] },
				{ name: 'left_eyebrow_G', series: [] },
				{ name: 'left_eyebrow_B', series: [] },
				{ name: 'right_eyebrow_R', series: [] },
				{ name: 'right_eyebrow_G', series: [] },
				{ name: 'right_eyebrow_B', series: [] }
			];
	
			for (let index = start; index <= currentTime; index++) {
				const time = this.convertToTimerFormat(index);
	
				let left_eyebrow_R;
				let left_eyebrow_G;
				let left_eyebrow_B;

				let right_eyebrow_R;
				let right_eyebrow_G;
				let right_eyebrow_B;
	
				if (index < this.data.left_eyebrow.length) {
					left_eyebrow_R = +(this.data.left_eyebrow[index][0]);
					left_eyebrow_G = +(this.data.left_eyebrow[index][1]);
					left_eyebrow_B = +(this.data.left_eyebrow[index][2]);
				} else {
					left_eyebrow_R = 0;
					left_eyebrow_G = 0;
					left_eyebrow_B = 0;
				}

				if (index < this.data.right_eyebrow.length) {
					right_eyebrow_R = +(this.data.right_eyebrow[index][0]);
					right_eyebrow_G = +(this.data.right_eyebrow[index][1]);
					right_eyebrow_B = +(this.data.right_eyebrow[index][2]);
				} else {
					right_eyebrow_R = 0;
					right_eyebrow_G = 0;
					right_eyebrow_B = 0;
				}
	
				dataChart[0].series.push({ name: time, value: left_eyebrow_R });
				dataChart[1].series.push({ name: time, value: left_eyebrow_G });
				dataChart[2].series.push({ name: time, value: left_eyebrow_B });
	
				dataChart[3].series.push({ name: time, value: right_eyebrow_R });
				dataChart[4].series.push({ name: time, value: right_eyebrow_G });
				dataChart[5].series.push({ name: time, value: right_eyebrow_B });
			}
	
			this.chartData = [...dataChart];
		}
		
		if (!this.ready) {
			this.ready = true;
		}
	}

	countUniqueStrings(arr: string[]) {
		if (!Array.isArray(arr)) {
			return 0;
		}
	
		const uniqueStrings = new Set();
		for (const item of arr) {
			if (typeof item === 'string') {
				uniqueStrings.add(item);
			}
		}
		return uniqueStrings.size;
	}

	arrangeStrings(arr: string[]) {
		if (!Array.isArray(arr)) {
			return [];
		}
	
		const uniqueStrings = [];
		let u_item = arr[0];
		let c = 1;
		let index = 0;

		uniqueStrings.push(u_item + ' * ' + c);
		for (const item of arr) {
			if (item === u_item) {
				c++;
			} else {
				uniqueStrings[index] = u_item + ' * ' + c;
				u_item = item;
				c = 1;
				index++;
				uniqueStrings.push([u_item, c]);
			}
		}

		uniqueStrings[index] = u_item + ' * ' + c;

		return uniqueStrings;
	}

	findTickTime() {
		const ticks = document.getElementsByClassName('tick');

		if (ticks.length > 0) {
			for (let index = 0; index < ticks.length; index++) {
				if (document.getElementsByClassName('tick')[index].childNodes[0].textContent === this.videoTime) {
					const inputString = document.getElementsByClassName('tick')[index].attributes[1].value;

					const pattern = /translate\((\d+\.\d+),\d+\)/;
					const match: RegExpExecArray | null = pattern.exec(inputString);
					
					if (match) {
						const result: number = parseFloat(match[1]);
						const elList = document.getElementsByClassName('tooltip-anchor')[0].attributes.getNamedItem('x');
						if (elList) {
							elList.nodeValue = result.toString();
						}
					}
				}
				break;
			}
		}
	}

	updateChart(currentTime: number) {
		if (this.area_type === 'head') {
			this.min = 0;
			this.max = 10;
		}
		
		this.videoTime = this.convertToTimerFormat(currentTime);
		if (currentTime > 20) {
			this.calculateChartData(currentTime);
		} else {
			this.calculateChartData(20);
		}
	}

	showChart() {
		if (this.area_type === 'head') {
			this.min = 0;
			this.max = 10;
		}
		this.ready = true;
		this.calculateChartData(20);
	}

	convertToTimerFormat(seconds: number) {
		if (seconds < 3600) {
			// Display "mm:ss" format for the first 60 minutes
			const minutes = Math.floor(seconds / 60);
			const formattedSeconds = (seconds % 60).toString().padStart(2, "0");
			const formattedMinutes = minutes.toString().padStart(2, "0");
			return `${formattedMinutes}:${formattedSeconds}`;
		} else {
			// After 60 minutes, switch to "hh:mm:ss" format
			const hours = Math.floor(seconds / 3600);
			const remainingSeconds = seconds % 3600;
			const minutes = Math.floor(remainingSeconds / 60);
			const formattedSeconds = (remainingSeconds % 60).toString().padStart(2, "0");
			const formattedMinutes = minutes.toString().padStart(2, "0");
			const formattedHours = hours.toString().padStart(2, "0");
			return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
		}
	}
}
